/**
 * RCC Surface #8 — Compliance Reports dashboard.
 *
 * Scheduled + ad-hoc compliance reports. Reads config.auditReportTemplates and
 * links into RAS for generation (RAS owns the actual report runner + log).
 */

import { Link } from 'react-router';
import { useConfig } from '@/contexts/ConfigContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

interface LegacyReport {
  code: string;
  label: string;
  cadence: string;
}

const LEGACY_REPORTS: LegacyReport[] = [
  { code: 'ZROCLS', label: 'Zero-balance closure report', cadence: 'Weekly' },
  { code: 'NEWOD', label: 'New overdraft report', cadence: 'Daily' },
  { code: 'NOFUND', label: 'Not-funded new accounts', cadence: 'Daily' },
  { code: 'NEWESI', label: 'New Estate Safekeeping Items (TBC)', cadence: 'Weekly' },
  { code: 'FSR-UB', label: 'Undeliverable / unclaimed balance weekly', cadence: 'Weekly' },
];

export function ComplianceReports(): JSX.Element {
  const { config } = useConfig();
  const auditTemplates = (config as { auditReportTemplates?: ReadonlyArray<{ code: string; name: string }> })
    .auditReportTemplates;

  return (
    <div>
      <SurfaceHeader
        title="Compliance Reports"
        scope="Legacy + modern compliance reports. Generation handed off to RAS (Audit Support) for chain-of-custody logging."
        surfaceNumber={8}
        role="FSR · Compliance oversight"
        actions={
          <Link to="/ras">
            <Button variant="primary" size="sm">
              Open RAS report runner
            </Button>
          </Link>
        }
      />

      <section style={panelStyle}>
        <h3 style={{ marginTop: 0 }}>Legacy compliance reports</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)', textAlign: 'left' }}>
              <th style={{ padding: 'var(--sp-3)' }}>Code</th>
              <th style={{ padding: 'var(--sp-3)' }}>Description</th>
              <th style={{ padding: 'var(--sp-3)' }}>Cadence</th>
              <th style={{ padding: 'var(--sp-3)' }} />
            </tr>
          </thead>
          <tbody>
            {LEGACY_REPORTS.map((r) => (
              <tr key={r.code} style={{ borderTop: '1px solid var(--ink-200)' }}>
                <td style={{ padding: 'var(--sp-3)', fontFamily: 'var(--font-mono)' }}>{r.code}</td>
                <td style={{ padding: 'var(--sp-3)' }}>{r.label}</td>
                <td style={{ padding: 'var(--sp-3)' }}>
                  <Pill variant="muted">{r.cadence}</Pill>
                </td>
                <td style={{ padding: 'var(--sp-3)', textAlign: 'right' }}>
                  <Link to={`/ras?templateCode=${encodeURIComponent(r.code)}`}>
                    <Button variant="link" size="sm">
                      Run →
                    </Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {auditTemplates && auditTemplates.length > 0 && (
        <section style={panelStyle}>
          <h3 style={{ marginTop: 0 }}>Configured audit templates ({auditTemplates.length})</h3>
          <ul style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-700)' }}>
            {auditTemplates.map((t) => (
              <li key={t.code}>
                <code style={{ color: 'var(--pcb-blue)' }}>{t.code}</code> — {t.name}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-7)',
  marginBottom: 'var(--sp-6)',
};
